import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { streamLLM, api } from "@/lib/api";
import StreamBox, { PlatformPicker, PrimaryButton, GhostButton, TextInput } from "@/components/StreamBox";
import TrendsPanel from "@/components/TrendsPanel";
import VoiceButton from "@/components/VoiceButton";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { Copy, Save } from "lucide-react";

export default function HookGenerator() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh: refreshSub } = useSubscription();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [tone, setTone] = useState("bold");
  const [count, setCount] = useState(5);
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);

  useEffect(() => {
    if (location.state?.topic) {
      setTopic(location.state.topic);
      toast.success(`Loaded trend: ${location.state.topic}`);
    }
  }, [location.state]);

  const handleError = (e) => {
    setStreaming(false);
    if (e === "QUOTA_EXCEEDED") {
      toast.error("Free daily limit hit. Upgrade to Pro for unlimited.", {
        action: { label: "Upgrade", onClick: () => navigate("/app/pricing") },
      });
    } else {
      toast.error(e);
    }
  };

  const run = async () => {
    if (!topic.trim()) return toast.error("Drop a topic first.");
    setOutput("");
    setStreaming(true);
    await streamLLM("/generate/hook", { topic, platform, tone, count: Number(count) }, {
      onDelta: (d) => setOutput((p) => p + d),
      onDone: () => { setStreaming(false); refreshSub(); },
      onError: handleError,
    });
  };

  const save = async () => {
    if (!output.trim()) return;
    try {
      await api.post("/library", { type: "hook", platform, input: topic, output });
      toast.success("Saved to Vault.");
    } catch (e) { toast.error("Save failed."); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    toast.success("Copied.");
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-8">
      <header className="border-b border-white/10 pb-6">
        <div className="label-eyebrow">// module 01</div>
        <h1 className="heading-display text-4xl sm:text-5xl mt-2">HOOK <span className="text-signal-yellow">FORGE</span></h1>
        <p className="font-mono text-xs text-zinc-500 mt-3">// inject a topic. extract scroll-stopping openers. ship.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-2 space-y-5">
          <TextInput
            data-testid="hook-topic"
            label="Topic / Angle"
            placeholder="e.g. why most productivity advice is a scam"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <VoiceButton testid="hook-voice" label="Voice topic" onText={(t) => setTopic((p) => (p ? p + " " + t : t))} />
            <span className="font-mono text-[10px] text-zinc-600">// or tap a trend →</span>
          </div>
          <div className="space-y-2">
            <span className="label-eyebrow block">Platform</span>
            <PlatformPicker value={platform} onChange={setPlatform} name="hook-platform" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-2">
              <span className="label-eyebrow block">Tone</span>
              <select
                data-testid="hook-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full bg-[#141414] border border-white/10 text-white p-3 font-mono text-sm focus:border-signal-yellow focus:outline-none"
              >
                <option value="bold">Bold</option>
                <option value="curious">Curious</option>
                <option value="contrarian">Contrarian</option>
                <option value="story">Story-driven</option>
                <option value="emotional">Emotional</option>
                <option value="data-driven">Data-driven</option>
              </select>
            </label>
            <label className="block space-y-2">
              <span className="label-eyebrow block">Count</span>
              <input
                data-testid="hook-count"
                type="number" min={1} max={10} value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-full bg-[#141414] border border-white/10 text-white p-3 font-mono text-sm focus:border-signal-yellow focus:outline-none"
              />
            </label>
          </div>
          <PrimaryButton data-testid="hook-generate" onClick={run} disabled={streaming} className="w-full">
            {streaming ? "Forging..." : "Forge hooks →"}
          </PrimaryButton>
        </div>

        {/* Output */}
        <div className="lg:col-span-3 space-y-3">
          <StreamBox text={output} streaming={streaming} label="Hook Output" />
          {output && !streaming && (
            <div className="flex gap-2">
              <GhostButton data-testid="hook-copy" onClick={copy}><Copy size={12} className="inline mr-2" />Copy</GhostButton>
              <GhostButton data-testid="hook-save" onClick={save}><Save size={12} className="inline mr-2" />Save to Vault</GhostButton>
            </div>
          )}
          <TrendsPanel onPick={(name) => { setTopic(name); toast.success(`Loaded: ${name}`); }} />
        </div>
      </div>
    </div>
  );
}
